import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AddPartRequestNoteRequest, PartNoteTarget, PartRequestNote } from '../../../models/part-request.model';
import { PartsRequestService } from '../../../services/parts-request.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { NoteType } from '../../../enums/note-type.enum';

@Component({
  selector: 'app-view-part-notes',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './view-part-notes.component.html',
  styleUrl: './view-part-notes.component.scss'
})
export class ViewPartNotesComponent implements OnInit {
  @Input() part!: PartNoteTarget;
  @Input() workOrderNumber: string = '';

  notes: PartRequestNote[] = [];
  isLoading = false;
  hasError = false;
  isSaving = false;
  showAddNote = false;

  readonly maxLength = 500;
  expandedNotes = new Set<string>();

  noteControl = new FormControl('');

  @ViewChild('noteInput') noteInputRef?: ElementRef<HTMLTextAreaElement>;

  private activeModal = inject(NgbActiveModal);
  private partsRequestService = inject(PartsRequestService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  ngOnInit(): void {
    void this.loadNotesAsync();
  }

  get regularNotes(): PartRequestNote[] {
    return this.notes.filter(n => n.noteTypeId === NoteType.Regular);
  }

  get rejectionNotes(): PartRequestNote[] {
    return this.notes.filter(n => n.noteTypeId === NoteType.Rejection);
  }

  get title(): string {
    if (this.showAddNote && this.notes.length === 0) return this.transloco.translate('parts.modals.viewNotes.addNotesTitle');
    return this.transloco.translate('parts.modals.viewNotes.notesTitle', {
      partNumber: this.part.partNumber,
      category: this.part?.category ?? ''
    }).trim();
  }

  isLong(content: string): boolean {
    return content.length > this.maxLength;
  }

  isExpanded(key: string): boolean {
    return this.expandedNotes.has(key);
  }

  toggleExpand(key: string): void {
    if (this.expandedNotes.has(key)) {
      this.expandedNotes.delete(key);
    } else {
      this.expandedNotes.add(key);
    }
  }

  displayContent(content: string, key: string): string {
    if (!this.isLong(content) || this.isExpanded(key)) return content;
    return content.slice(0, this.maxLength).trimEnd() + '...';
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  onAddNote(): void {
    this.showAddNote = true;
    setTimeout(() => {
      this.noteInputRef?.nativeElement?.focus();
      this.noteInputRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  async onSave(): Promise<void> {
    const content = this.noteControl.value?.trim() ?? '';
    if (!content || this.isSaving) return;

    this.isSaving = true;
    const request: AddPartRequestNoteRequest = { content, noteTypeId: NoteType.Regular };

    try {
      await this.partsRequestService.addPartRequestNoteAsync(this.part.id, request);
      this.noteControl.setValue('');
      this.showAddNote = false;
      await this.loadNotesAsync();
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isSaving = false;
    }
  }

  private async loadNotesAsync(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const response = await this.partsRequestService.getPartRequestNotesAsync(this.part.id);
      this.notes = response.data ?? [];
      if (this.notes.length === 0) {
        this.showAddNote = true;
      }
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => { this.hasError = true; }
      });
    } finally {
      this.isLoading = false;
    }
  }
}